import { type SchedulerProConfig } from '@bryntum/schedulerpro'
import { addSeconds } from 'date-fns/esm'

import { MoveOrdersPayload } from '@/components/modals/MoveOrdersModal'
import { selectDayRangeInfo } from '@/features/domain/scheduler'
import { selectCalendarRangeInterval } from '@/features/domain/ui'
import { ControlledNavigateFunction } from '@/routing'
import { store } from '@/store'

import { getProject } from '../../atoms/project'
import { EventRenderer } from '../../components/Scheduler/components/EventRenderer'

import { onBeforeEventDrag } from './events/onBeforeEventDrag'
import { createOnBeforeEventDropFinalize } from './events/onBeforeEventDropFinalize'
import { onBeforeEventResize } from './events/onBeforeEventResize'
import { onBeforeEventResizeFinalize } from './events/onBeforeEventResizeFinalize'
import { onBeforeToggleGroup } from './events/onBeforeToggleGroup'
import { createOnCellClick } from './events/onCellClick'
import { createOnEventDblClick } from './events/onEventDblClick'
import { onEventMouseDown } from './events/onEventMouseDown'
import { onEventPartialResize } from './events/onEventPartialResize'
import { onEventResizeEnd } from './events/onEventResizeEnd'
import { onRenderRow } from './events/onRenderRow'
import { onResize } from './events/onResize'
import { onSelectionChange } from './events/onSelectionChange'
import { onSplitterCollapseClick } from './events/onSplitterCollapseClick'
import { onSplitterExpandClick } from './events/onSplitterExpandClick'

import { createColumnStore } from './columnStore'
import { eventLayout } from './eventLayout'
import { createFeatures } from './features'
import { isEventSelectable } from './isEventSelectable'
import { navigator } from './navigator'
import { createSubGridConfigs } from './subGridConfigs'

export function createSchedulerConfig(
  appendTo: string,
  navigate: ControlledNavigateFunction<uui.routing.Routing | uui.routing.Setup>,
  showMoveOrdersModal: (payload: MoveOrdersPayload) => void,
  onHeaderMenuBeforeShow: SchedulerProConfig['onHeaderMenuBeforeShow'],
): Partial<SchedulerProConfig> {
  const state = store.getState()

  const { dayStartOffset, dayLength } = selectDayRangeInfo(state)
  const calendarRangeInterval = selectCalendarRangeInterval(state)

  const onBeforeEventDropFinalize = createOnBeforeEventDropFinalize(showMoveOrdersModal)
  const onEventDblClick = createOnEventDblClick(navigate)
  const onCellClick = createOnCellClick(navigate)

  const subGridConfigs = createSubGridConfigs()

  const startDate = addSeconds(calendarRangeInterval.start, dayStartOffset)
  const endDate = addSeconds(calendarRangeInterval.start, dayStartOffset + dayLength)

  const features = createFeatures()
  const columns = createColumnStore()
  const project = getProject()

  return {
    displaySchedulingIssueResolutionPopup: false,
    updateTimelineContextOnScroll: false,
    ignoreDomEventsWhileScrolling: true,
    selectResourceOnEventNavigate: false,
    selectResourceOnScheduleClick: false,
    preserveScrollOnDatasetChange: true,
    onBeforeEventResizeFinalize,
    deselectAllOnScheduleClick: false,
    zoomKeepsOriginalTimespan: true,
    zoomOnTimeAxisDoubleClick: false,
    onBeforeEventDropFinalize,
    onSplitterCollapseClick,
    eventSelectionDisabled: true, // NOTE: This is set to true to prevent the default selection behavior of the scheduler from interfering with the custom selection behavior.
    onHeaderMenuBeforeShow,
    createEventOnDblClick: false,
    enableEventAnimations: false,
    onSplitterExpandClick,
    onEventPartialResize,
    onBeforeToggleGroup,
    useInitialAnimation: false,
    onBeforeEventResize,
    milestoneLayoutMode: 'data',
    autoAdjustTimeAxis: false,
    managedEventSizing: false,
    transitionDuration: 0,
    onBeforeEventDrag,
    isEventSelectable,
    onSelectionChange,
    onEventMouseDown,
    multiEventSelect: true,
    onEventResizeEnd,
    onEventDblClick,
    milestoneAlign: 'start',
    resourceMargin: 0,
    subGridConfigs,
    destroyStores: false,
    eventRenderer: EventRenderer,
    stickyHeaders: false,
    allowOverlap: true,
    allowCreate: false,
    eventLayout,
    onCellClick,
    onRenderRow,
    viewPreset: 'preset-level-0',
    rowHeight: 23,
    barMargin: 0,
    //@ts-expect-error
    navigator,
    startDate,
    appendTo,
    features,
    onResize,
    columns,
    endDate,
    project,
  }
}
